/**
 * Plot colours.
 *
 * Both modes are *selected*, not derived — the dark column is the same eight
 * hues re-stepped for the dark surface. The ordering is the colour-blindness
 * safety mechanism: it was validated adjacent-pairwise against this app's
 * actual surfaces (paper #ffffff, night #18181b), so slots must be assigned in
 * order and never cycled.
 *
 * Three light-mode hues sit under 3:1 against paper, which is why the legend is
 * mandatory for two or more series — identity never rests on colour alone.
 */

export type ThemeMode = "paper" | "night";

const SERIES_LIGHT = [
  "#2c5aa8", // ink blue — the brand accent, so one-series figures match the UI
  "#eb6834", // orange
  "#1baf7a", // aqua
  "#eda100", // yellow
  "#e87ba4", // magenta
  "#008300", // green
  "#4a3aa7", // violet
  "#e34948", // red
] as const;

/**
 * The same eight hues re-stepped for the dark surface — a selected palette,
 * not an automatic lightening of the light one. Slot 1 is a brighter blue than
 * the UI accent because the accent itself is too pale and too low-chroma to
 * carry data: it fails both the lightness band and the chroma floor.
 */
const SERIES_DARK = [
  "#3987e5", // blue
  "#d95926", // orange
  "#199e70", // aqua
  "#c98500", // yellow
  "#d55181", // magenta
  "#008300", // green
  "#9085e9", // violet
  "#e66767", // red
] as const;

export const MAX_SERIES_COLORS = SERIES_LIGHT.length;

export function seriesColor(index: number, mode: ThemeMode): string {
  const slots = mode === "night" ? SERIES_DARK : SERIES_LIGHT;
  // Past the eighth series identity has to come from labels, not new hues.
  return slots[index % slots.length]!;
}

/** Single-hue indigo, light → dark. Magnitude only. */
const SEQUENTIAL_BLUE = [
  "#e0e7ff", "#c7d2fe", "#a5b4fc", "#818cf8", "#6366f1",
  "#4f46e5", "#4338ca", "#3730a3", "#312e81", "#1e1b4b",
  "#172554", "#0f172a", "#020617",
] as const;

/** Cool/warm poles with a genuinely neutral middle, for signed fields. */
const DIVERGING = {
  low: "#1e1b4b",
  midLight: "#f8fafc",
  midDark: "#27272a",
  high: "#7f1d1d",
} as const;

const PLASMA = [
  "#fde68a", "#fcd34d", "#fbbf24", "#f59e0b", "#ea580c",
  "#dc2626", "#be185d", "#7e22ce",
] as const;

export type ColorScaleName = "viridis" | "plasma" | "diverging";

/**
 * Map a normalized value to a colour.
 *
 * `t` is expected in [0, 1]; out-of-range values clamp rather than wrap, so a
 * stray sample can't produce a colour that reads as the opposite extreme.
 */
export function rampColor(
  scale: ColorScaleName,
  t: number,
  mode: ThemeMode,
): string {
  const clamped = Math.min(1, Math.max(0, Number.isFinite(t) ? t : 0));

  if (scale === "diverging") {
    const mid = mode === "night" ? DIVERGING.midDark : DIVERGING.midLight;
    return clamped < 0.5
      ? mixHex(DIVERGING.low, mid, clamped * 2)
      : mixHex(mid, DIVERGING.high, (clamped - 0.5) * 2);
  }

  const stops = scale === "plasma" ? PLASMA : SEQUENTIAL_BLUE;
  return sampleRamp(stops, clamped);
}

function sampleRamp(stops: readonly string[], t: number): string {
  const scaled = t * (stops.length - 1);
  const lower = Math.floor(scaled);
  const upper = Math.min(lower + 1, stops.length - 1);
  return mixHex(stops[lower]!, stops[upper]!, scaled - lower);
}

/** Linear sRGB-space blend. Good enough between neighbouring ramp stops. */
export function mixHex(from: string, to: string, t: number): string {
  const a = parseHex(from);
  const b = parseHex(to);
  const weight = Math.min(1, Math.max(0, t));

  const channel = (index: number): number =>
    Math.round(a[index]! + (b[index]! - a[index]!) * weight);

  return `#${[0, 1, 2].map((i) => channel(i).toString(16).padStart(2, "0")).join("")}`;
}

function parseHex(hex: string): readonly [number, number, number] {
  const clean = hex.replace("#", "");
  return [
    parseInt(clean.slice(0, 2), 16),
    parseInt(clean.slice(2, 4), 16),
    parseInt(clean.slice(4, 6), 16),
  ];
}

/**
 * Chart chrome. Kept out of CSS so SVG-only consumers get the same values.
 *
 * `muted` is tick-label *text* and so tracks `--ink-muted` exactly — it has to
 * clear 4.5:1 against the plot surface like any other reading. `grid` and
 * `axis` are rules, not text, and stay light on purpose: a grid that meets text
 * contrast is a grid that competes with the curve drawn on it.
 */
export const CHROME = {
  paper: {
    surface: "#ffffff",
    grid: "#e5e1d8",
    axis: "#cdc7ba",
    muted: "#6b665c",
    ink: "#17150f",
  },
  night: {
    surface: "#1a1917",
    grid: "#292724",
    axis: "#3c3934",
    muted: "#8b857a",
    ink: "#edeae3",
  },
} as const;
